import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Pagination,
  Skeleton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  SchoolOutlined as SchoolIcon,
  CalendarMonthOutlined as CalendarIcon,
  CardMembershipOutlined as MembershipIcon,
  EventAvailableOutlined as BookingIcon,
} from "@mui/icons-material";
import { useMyInstituteAssignments } from "../../../hooks/useTrainers";
import InstituteBookingModal from "./InstituteBookingModal";

// Helper to format ISO date string
const formatDate = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return String(isoString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return String(isoString);
  }
};

// Check if a date is expired
const isDateExpired = (dateStr) => {
  if (!dateStr) return false;
  try {
    const d = new Date(dateStr);
    return !isNaN(d.getTime()) && d < new Date();
  } catch {
    return false;
  }
};

export default function AssignedInstitutesTable({ onNavigateToEDP }) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Debounce search query (400ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Query hook
  const { data, isLoading, refetch } = useMyInstituteAssignments({
    search: debouncedSearch,
    page,
    limit,
  });

  const institutes = data?.data || [];
  const totalRecords = data?.total || 0;
  const totalPages = data?.total_pages || Math.ceil(totalRecords / limit) || 1;

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleOpenBooking = () => {
    setIsBookingModalOpen(true);
  };

  const handleCloseBooking = () => {
    setIsBookingModalOpen(false);
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
      {/* Header section with Title & Top-Right Controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EEF2FF",
                color: "#4F46E5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  color: "#1E293B",
                  fontSize: { xs: "18px", sm: "20px" },
                  lineHeight: 1.2,
                }}
              >
                Assigned Institutes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "13px",
                  mt: 0.2,
                }}
              >
                Institutes mapped to your trainer profile
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Top-Right Controls: Search Box + Single Booking Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {/* Search Box */}
          <TextField
            placeholder="Search institute name..."
            size="small"
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              width: { xs: "100%", sm: 260 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13.5px",
                bgcolor: "white",
                "&:hover fieldset": { borderColor: "#6366F1" },
                "&.Mui-focused fieldset": { borderColor: "#4F46E5" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ p: 0.5, color: "#94A3B8", "&:hover": { color: "#64748B" } }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Single Top-Right Booking Button */}
          <Button
            variant="contained"
            startIcon={<BookingIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenBooking}
            sx={{
              bgcolor: "#4F46E5",
              color: "white",
              textTransform: "none",
              fontSize: "13.5px",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              py: 0.85,
              boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
              "&:hover": {
                bgcolor: "#4338CA",
                boxShadow: "0 4px 6px rgba(79, 70, 229, 0.3)",
              },
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            Book Slot
          </Button>
        </Box>
      </Box>

      {/* Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: "white",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <Table sx={{ minWidth: 900 }} aria-label="assigned institutes table">
          <TableHead sx={{ bgcolor: "#F8FAFC" }}>
            <TableRow sx={{ borderBottom: "1px solid #E2E8F0" }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 50,
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  minWidth: 200,
                }}
              >
                Institute Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  minWidth: 140,
                }}
              >
                Educator Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 180,
                }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 120,
                }}
              >
                Phone Number
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 150,
                }}
              >
                Membership Plan
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "12.5px",
                  color: "#475569",
                  fontFamily: "'Poppins', sans-serif",
                  py: 1.8,
                  width: 130,
                }}
              >
                Expire Date
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              // Loading Skeletons
              Array.from(new Array(5)).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  <TableCell><Skeleton width={20} /></TableCell>
                  <TableCell>
                    <Skeleton width="60%" height={24} />
                    <Skeleton width="30%" height={16} />
                  </TableCell>
                  <TableCell><Skeleton width={120} height={20} /></TableCell>
                  <TableCell><Skeleton width={140} height={20} /></TableCell>
                  <TableCell><Skeleton width={90} height={20} /></TableCell>
                  <TableCell><Skeleton width={100} height={28} /></TableCell>
                  <TableCell><Skeleton width={90} /></TableCell>
                </TableRow>
              ))
            ) : institutes.length === 0 ? (
              // Empty State
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        bgcolor: "#F1F5F9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#94A3B8",
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {debouncedSearch ? "No matching institutes found" : "No Assigned Institutes"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#94A3B8",
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: "13px",
                        maxWidth: 360,
                        textAlign: "center",
                      }}
                    >
                      {debouncedSearch
                        ? `No institute matching "${debouncedSearch}". Try another search keyword.`
                        : "You do not have any institutes assigned to your profile yet."}
                    </Typography>
                    {debouncedSearch && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleClearSearch}
                        sx={{
                          textTransform: "none",
                          fontSize: "12.5px",
                          fontFamily: "'Poppins', sans-serif",
                          mt: 1,
                          borderColor: "#CBD5E1",
                          color: "#475569",
                        }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              institutes.map((inst, index) => {
                const sNo = (page - 1) * limit + index + 1;
                const expired = isDateExpired(inst.expire_date);

                return (
                  <TableRow
                    key={inst.institute_id || index}
                    sx={{
                      "&:hover": { bgcolor: "#F8FAFC" },
                      transition: "background-color 0.15s ease",
                      borderBottom: "1px solid #F1F5F9",
                    }}
                  >
                    {/* S.No */}
                    <TableCell
                      sx={{
                        color: "#64748B",
                        fontSize: "13px",
                        fontWeight: 500,
                        fontFamily: "'Poppins', sans-serif",
                        py: 2,
                      }}
                    >
                      {sNo}
                    </TableCell>

                    {/* Institute Name & Type */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "#1E293B",
                            fontSize: "14px",
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {inst.institute_name || "—"}
                        </Typography>
                        {inst.institute_type && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Chip
                              label={inst.institute_type}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "11px",
                                fontWeight: 500,
                                bgcolor: "#F1F5F9",
                                color: "#475569",
                                borderRadius: "4px",
                                fontFamily: "'Poppins', sans-serif",
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    {/* Educator Name */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#1E293B",
                          fontSize: "13.5px",
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        {inst.educator_name || "—"}
                      </Typography>
                    </TableCell>

                    {/* Educator Email */}
                    <TableCell sx={{ py: 2, maxWidth: 200 }}>
                      <Typography
                        variant="body2"
                        title={inst.educator_email || ""}
                        sx={{
                          color: "#475569",
                          fontSize: "13px",
                          fontFamily: "'Poppins', sans-serif",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {inst.educator_email || "—"}
                      </Typography>
                    </TableCell>

                    {/* Educator Phone */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#475569",
                          fontSize: "13px",
                          fontFamily: "'Poppins', sans-serif",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {inst.educator_phone || "—"}
                      </Typography>
                    </TableCell>

                    {/* Membership Plan */}
                    <TableCell sx={{ py: 2 }}>
                      {inst.membership_name ? (
                        <Chip
                          icon={<MembershipIcon style={{ fontSize: 14, color: "#4338CA" }} />}
                          label={inst.membership_name}
                          size="small"
                          sx={{
                            bgcolor: "#EEF2FF",
                            color: "#4338CA",
                            fontWeight: 600,
                            fontSize: "12px",
                            fontFamily: "'Poppins', sans-serif",
                            border: "1px solid #E0E7FF",
                            borderRadius: "6px",
                            px: 0.5,
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "#94A3B8", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Expire Date */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: expired ? "#EF4444" : "#64748B" }} />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: expired ? "#DC2626" : "#334155",
                              fontFamily: "'Poppins', sans-serif",
                            }}
                          >
                            {formatDate(inst.expire_date)}
                          </Typography>
                          {expired && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#EF4444", fontSize: "10.5px", fontWeight: 600 }}
                            >
                              Expired
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination & Footer Controls */}
      {!isLoading && institutes.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mt: 2.5,
            px: 1,
          }}
        >
          {/* Rows per page selector */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
            >
              Rows per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={limit}
                onChange={handleLimitChange}
                sx={{
                  height: 32,
                  fontSize: "13px",
                  borderRadius: "6px",
                  bgcolor: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E2E8F0",
                  },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="body2"
              sx={{ color: "#94A3B8", fontSize: "13px", fontFamily: "'Poppins', sans-serif" }}
            >
              Showing {Math.min((page - 1) * limit + 1, totalRecords)} -{" "}
              {Math.min(page * limit, totalRecords)} of {totalRecords}
            </Typography>
          </Box>

          {/* Pagination */}
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="medium"
            sx={{
              "& .MuiPaginationItem-root": {
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                borderRadius: "6px",
                "&.Mui-selected": {
                  bgcolor: "#4F46E5",
                  color: "white",
                  "&:hover": { bgcolor: "#4338CA" },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Booking Action Modal */}
      <InstituteBookingModal
        open={isBookingModalOpen}
        onClose={handleCloseBooking}
        institutes={institutes}
        onSuccess={(msg) => {
          showSnackbar(msg || "Slot booked successfully!", "success");
          refetch();
        }}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
